import React from "react";
import { ActionIcon, Group, Text, Tooltip } from "@mantine/core";
import {
  IconDownload,
  IconExternalLink,
  IconFile,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileSpreadsheet,
  IconHistory,
  IconPencil,
  IconPhoto,
  IconTrash,
} from "@tabler/icons-react";
import { SalonDocument } from "../../types/salonDocument";
import {
  documentCardTitle,
  documentListCategoryFromMime,
  documentListDateShort,
  documentUploaderDisplayName,
  formatDocumentByteSize,
  isMarkupableImageContentType,
  type DocumentListCategory,
} from "../../utils/clientDocumentUploadKey";
import styles from "./ClientDocumentsTab.module.css";

interface ClientDocumentsTableProps {
  documents: SalonDocument[];
  readOnly: boolean;
  onOpenVersions: (doc: SalonDocument) => void;
  onDelete: (doc: SalonDocument) => void;
  onDownload: (doc: SalonDocument) => void;
  onMarkup?: (doc: SalonDocument) => void;
}

function categoryIcon(category: DocumentListCategory): React.ReactNode {
  const wrap = (node: React.ReactNode, className: string) => (
    <div className={`${styles.docTypeIcon} ${className}`}>{node}</div>
  );
  switch (category) {
    case "IMAGE":
      return wrap(
        <IconPhoto size={22} stroke={1.75} />,
        styles.docTypeIconImage,
      );
    case "PDF":
      return wrap(
        <IconFileTypePdf size={22} stroke={1.75} />,
        styles.docTypeIconPdf,
      );
    case "SPREADSHEET":
      return wrap(
        <IconFileSpreadsheet size={22} stroke={1.75} />,
        styles.docTypeIconSheet,
      );
    case "DOCUMENT":
      return wrap(
        <IconFileTypeDoc size={22} stroke={1.75} />,
        styles.docTypeIconDoc,
      );
    default:
      return wrap(<IconFile size={22} stroke={1.75} />, styles.docTypeIconFile);
  }
}

export default function ClientDocumentsTable({
  documents,
  readOnly,
  onOpenVersions,
  onDelete,
  onDownload,
  onMarkup,
}: ClientDocumentsTableProps) {
  return (
    <div className={styles.docList}>
      {documents.map((doc) => {
        const category = documentListCategoryFromMime(doc.currentContentType);
        const uploader = documentUploaderDisplayName(doc);
        const metaLine = [
          category,
          formatDocumentByteSize(doc.currentFileSize),
          documentListDateShort(doc.currentCreatedDt),
          uploader ? `By ${uploader}` : "By —",
        ].join(" • ");

        return (
          <div
            key={doc.documentKey}
            className={styles.docCard}
            tabIndex={0}
            role="group"
            aria-label={`Document ${documentCardTitle(doc)}`}
          >
            {categoryIcon(category)}
            <div className={styles.docCardMain}>
              <Text
                size="sm"
                fw={600}
                lineClamp={2}
                className={styles.docCardTitle}
              >
                {documentCardTitle(doc)}
              </Text>
              <Text size="sm" c="dimmed" className={styles.docCardMeta}>
                {metaLine}
              </Text>
            </div>
            <Group
              gap={10}
              wrap="nowrap"
              className={styles.docCardActions}
              justify="flex-end"
            >
              {doc.currentUrl ? (
                <Tooltip label="Open">
                  <ActionIcon
                    component="a"
                    href={doc.currentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    color="teal"
                    className={styles.docCardActionIcon}
                    aria-label="Open document"
                  >
                    <IconExternalLink stroke={1.65} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              {onMarkup &&
              doc.currentUrl &&
              isMarkupableImageContentType(doc.currentContentType) ? (
                <Tooltip label="Markup">
                  <ActionIcon
                    variant="subtle"
                    color="teal"
                    className={styles.docCardActionIcon}
                    aria-label="Markup image"
                    onClick={() => onMarkup(doc)}
                  >
                    <IconPencil stroke={1.65} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              <Tooltip label="Download">
                <ActionIcon
                  variant="subtle"
                  color="teal"
                  className={styles.docCardActionIcon}
                  aria-label="Download document"
                  onClick={() => onDownload(doc)}
                >
                  <IconDownload stroke={1.65} />
                </ActionIcon>
              </Tooltip>
              {!readOnly && (
                <Tooltip label="Versions">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    className={styles.docCardActionIcon}
                    aria-label="Version history"
                    onClick={() => onOpenVersions(doc)}
                  >
                    <IconHistory stroke={1.65} />
                  </ActionIcon>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip label="Delete">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    className={styles.docCardActionIcon}
                    aria-label="Delete document"
                    onClick={() => onDelete(doc)}
                  >
                    <IconTrash stroke={1.65} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </div>
        );
      })}
    </div>
  );
}
